/**
 * Sektionen Materialausgaben und Verpflegung.
 * Aus pages/ProjektDetailPage.tsx herausgelöst (Plan Projekt-Tracking, Etappe 2) —
 * verschoben, nicht umgeschrieben.
 */
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useProjektAusgaben, useCreateAusgabe, useUpdateAusgabe, useDeleteAusgabe, useTeamMembers, useTeamOwners, useProjektBelege } from "../../hooks/usePlanung";
import { type Dokument } from "../DokumentLightbox";
import type { Ausgabe, ProjektBeleg } from "../../lib/types";
import { formatEur } from "../../lib/planer";
import { Sektion } from "./ProjektSektion";
import { heute, zahl } from "./seiteHilfen";

/* ══ Ausgaben (Material bzw. Verpflegung) ════════════════════════════════ */

export function AusgabenSektion({
  projektId,
  typ,
  titel,
  onZeigen,
  anker,
}: {
  projektId: string;
  typ: "material" | "verpflegung" | "personal";
  titel: string;
  onZeigen: (d: Dokument) => void;
  anker?: string;
}) {
  const { data: alle = [] } = useProjektAusgaben(projektId);
  const { data: owners = [] } = useTeamOwners();
  const { data: members = [] } = useTeamMembers();
  const create = useCreateAusgabe(projektId);
  const update = useUpdateAusgabe(projektId);
  const del = useDeleteAusgabe(projektId);

  const [editId, setEditId] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [loeschen, setLoeschen] = useState<string | null>(null);
  const { data: belege = [] } = useProjektBelege(projektId);

  // Drei Sichten auf dieselbe Tabelle. Helferbelege sind nur der
  // Rechnungsnachweis; die Personalkosten entstehen aus Stunden/Pauschalen.
  const zeilen = useMemo(
    () =>
      alle.filter((a) => {
        if (typ === "verpflegung") return a.ausgabe_typ === "verpflegung";
        if (typ === "personal") return a.ausgabe_typ === "helfer";
        return !["verpflegung", "helfer"].includes(a.ausgabe_typ);
      }),
    [alle, typ],
  );
  const summe = zeilen.reduce((s, a) => s + Number(a.netto || 0), 0);
  const helfer = members.filter((m) => m.active !== false && m.role !== "inhaber");

  const speichern = (body: Record<string, unknown>, ausgabeId?: string) => {
    setFehler(null);
    const optionen = {
      onSuccess: () => { setEditId(null); setNeuOffen(false); },
      onError: (e: unknown) => setFehler(e instanceof Error ? e.message : String(e)),
    };
    if (ausgabeId) update.mutate({ id: ausgabeId, patch: body }, optionen);
    else create.mutate(body as never, optionen);
  };

  return (
    <Sektion
      titel={titel}
      anker={anker}
      kopfRechts={
        <>
          {zeilen.length} {zeilen.length === 1 ? "Posten" : "Posten"} · Σ {formatEur(summe, { round: true })}
        </>
      }
    >
      {fehler && <div className="vg-banner is-warn">{fehler}</div>}
      {typ === "personal" && (
        <div className="vg-detail-section-hint">
          Rechnungsnachweise zu den oben erfassten Stunden und Pauschalen. Sie werden nicht
          zusätzlich als Materialkosten in der Gewinnermittlung abgezogen.
        </div>
      )}

      {zeilen.length === 0 && !neuOffen ? (
        <div className="vg-detail-section-hint">Noch keine Posten erfasst.</div>
      ) : (
        <table className="vg-tabelle">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Lieferant</th>
              <th>Beschreibung</th>
              <th>Bezahlt von</th>
              <th className="is-num">Netto</th>
              <th className="is-aktion" />
            </tr>
          </thead>
          <tbody>
            {zeilen.map((a) =>
              editId === a.id ? (
                <AusgabeForm
                  key={a.id}
                  ausgabe={a}
                  typ={typ}
                  owners={owners}
                  helfer={helfer}
                  busy={update.isPending}
                  onAbbrechen={() => setEditId(null)}
                  onSpeichern={(body) => speichern(body, a.id)}
                />
              ) : (
                <tr key={a.id}>
                  <td>{a.ausgabedatum || "—"}</td>
                  <td>{a.lieferant || "—"}</td>
                  <td className="is-muted">
                    {a.beschreibung || "—"}
                    {a.helfer_name ? ` · ${a.helfer_name}` : ""}
                  </td>
                  <td className="is-muted">
                    {a.zahlungsweg === "inhaber" ? a.inhaber_name || "Inhaber" : "GbR-Konto"}
                  </td>
                  <td className="is-num">{formatEur(a.netto, { round: true })}</td>
                  <td className="is-aktion">
                    {/* Aus einem Beleg gebuchte Zeilen sahen aus wie handgetippte —
                        der Weg zum Belegbild fehlte ganz. */}
                    {a.beleg_id && (
                      <button
                        type="button"
                        className="vg-icon-btn"
                        title="Beleg anzeigen"
                        onClick={() => onZeigen({
                          titel: a.lieferant || a.beschreibung || "Beleg",
                          unterzeile: `${a.ausgabedatum || ""} · ${formatEur(a.netto, { round: true })}`,
                          url: `/api/kontor/belege/${a.beleg_id}/pdf`,
                          belegId: a.beleg_id,
                        })}
                      >
                        ↗
                      </button>
                    )}
                    <button type="button" className="vg-icon-btn" title="Bearbeiten" onClick={() => setEditId(a.id)}>
                      ✎
                    </button>
                    <button
                      type="button"
                      className="vg-icon-btn is-danger"
                      title="Posten entfernen"
                      onClick={() => setLoeschen(a.id)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ),
            )}
            {neuOffen && (
              <AusgabeForm
                typ={typ}
                owners={owners}
                helfer={helfer}
                busy={create.isPending}
                onAbbrechen={() => setNeuOffen(false)}
                onSpeichern={(body) => speichern(body)}
              />
            )}
          </tbody>
        </table>
      )}

      {!neuOffen && typ !== "personal" && (
        <button type="button" className="vg-btn vg-btn-ghost" style={{ marginTop: 10 }} onClick={() => setNeuOffen(true)}>
          + Posten hinzufügen
        </button>
      )}

      {loeschen && (() => {
        const posten = zeilen.find((z) => z.id === loeschen);
        const beleg = posten?.beleg_id ? belege.find((b) => b.id === posten.beleg_id) : undefined;
        return (
          <PostenLoeschen
            posten={posten}
            beleg={beleg}
            laeuft={del.isPending}
            onAbbrechen={() => setLoeschen(null)}
            onLoeschen={() => del.mutate(loeschen, { onSuccess: () => setLoeschen(null) })}
          />
        );
      })()}
    </Sektion>
  );
}

/**
 * Was genau verschwindet hier?
 *
 * Ein Klick aufs Kreuz löschte den Posten sofort und wortlos. Bei Posten, die
 * aus einem Beleg gebucht wurden, sieht das aus, als wäre der Beleg mit weg —
 * er ist es nicht: Datei, Belegzeile, Bankzeilen-Zuordnung und DATEV-Stand
 * bleiben unangetastet, nur die Projektbuchung fällt weg. Genau das steht jetzt
 * im Dialog, samt Status des Belegs, damit niemand raten muss.
 */
export function PostenLoeschen({ posten, beleg, laeuft, onAbbrechen, onLoeschen }: {
  posten?: Ausgabe;
  beleg?: ProjektBeleg;
  laeuft: boolean;
  onAbbrechen: () => void;
  onLoeschen: () => void;
}) {
  return createPortal(
    <div className="vg-overlay" role="dialog" aria-modal="true">
      <button type="button" className="vg-overlay-backdrop" aria-label="Abbrechen" onClick={onAbbrechen} />
      <div className="vg-overlay-panel is-schmal">
        <div className="vg-overlay-head">
          <h2 className="vg-overlay-title">Posten entfernen?</h2>
        </div>
        <p className="vg-detail-section-hint">
          <strong>{posten?.beschreibung || posten?.lieferant || "Posten"}</strong>
          {posten ? ` · ${formatEur(posten.netto, { round: true })}` : ""}
        </p>
        {beleg ? (
          <div className="vg-banner">
            <p><strong>Der Beleg bleibt erhalten.</strong> Entfernt wird nur die Buchung auf dieses Projekt — die Belegdatei, der Eintrag im Belegregister und die Bankzeilen-Zuordnung bleiben unverändert.</p>
            <ul className="vg-beleg-status">
              <li>Beleg: {beleg.lieferant || "ohne Lieferant"} · {beleg.datum || "ohne Datum"}</li>
              <li>Status: {beleg.status}</li>
              <li>{beleg.kontobewegung_id ? "Einer Bankzeile zugeordnet" : "Keiner Bankzeile zugeordnet"}</li>
            </ul>
            <p className="vg-detail-section-hint">
              Achtung: Solange Projekt und Ausgabeart am Beleg stehen bleiben, bucht ihn die
              Belegverarbeitung beim nächsten Bearbeiten wieder auf dieses Projekt. Dauerhaft
              lösen lässt sich die Zuordnung im Belegregister unter Finanzen.
            </p>
          </div>
        ) : (
          <p className="vg-detail-section-hint">Dieser Posten wurde von Hand erfasst; es hängt kein Beleg daran.</p>
        )}
        <div className="vg-overlay-actions">
          <button type="button" className="vg-btn vg-btn-ghost" onClick={onAbbrechen} disabled={laeuft}>Behalten</button>
          <button type="button" className="vg-btn-danger" onClick={onLoeschen} disabled={laeuft}>
            {laeuft ? "Entfernt …" : "Posten entfernen"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function AusgabeForm({
  ausgabe,
  typ,
  owners,
  helfer,
  busy,
  onAbbrechen,
  onSpeichern,
}: {
  ausgabe?: Ausgabe;
  typ: "material" | "verpflegung" | "personal";
  owners: Array<{ id: string; name: string }>;
  helfer: Array<{ id: string; name: string }>;
  busy: boolean;
  onAbbrechen: () => void;
  onSpeichern: (body: Record<string, unknown>) => void;
}) {
  const [f, setF] = useState({
    ausgabedatum: ausgabe?.ausgabedatum || heute(),
    lieferant: ausgabe?.lieferant || "",
    beschreibung: ausgabe?.beschreibung || "",
    netto: ausgabe ? String(ausgabe.netto) : "",
    // Bestandszeilen koennen aus dem Cache noch "gbr_konto" liefern — auf den
    // kanonischen Wert ziehen, sonst zeigt das <select> ein leeres Feld an und
    // ein unbedachtes Speichern kippt die Zeile auf "Inhaber".
    zahlungsweg: ausgabe?.zahlungsweg === "inhaber" ? "inhaber" : "gbr",
    inhaber_id: ausgabe?.inhaber_id || owners[0]?.id || "",
    ausgabe_typ: ausgabe?.ausgabe_typ || (typ === "verpflegung" ? "verpflegung" : "lieferant"),
    helfer_id: ausgabe?.helfer_id || "",
  });
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));
  const betrag = zahl(f.netto);
  const kannSpeichern = betrag != null && betrag >= 0 && !!f.ausgabedatum && !busy;

  return (
    <tr className="vg-tabelle-form">
      <td>
        <input type="date" value={f.ausgabedatum} onChange={(e) => set("ausgabedatum", e.target.value)} />
      </td>
      <td>
        <input value={f.lieferant} onChange={(e) => set("lieferant", e.target.value)} placeholder="Lieferant" />
      </td>
      <td>
        <input
          value={f.beschreibung}
          onChange={(e) => set("beschreibung", e.target.value)}
          placeholder="Beschreibung"
        />
        {typ === "material" && (
          <div className="vg-tabelle-form-zusatz">
            <select value={f.ausgabe_typ} onChange={(e) => set("ausgabe_typ", e.target.value)}>
              <option value="lieferant">Lieferant</option>
              <option value="betrieb">Betrieb</option>
              <option value="helfer">Helfer</option>
            </select>
            {f.ausgabe_typ === "helfer" && (
              <select value={f.helfer_id} onChange={(e) => set("helfer_id", e.target.value)}>
                <option value="">Helfer wählen…</option>
                {helfer.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            )}
          </div>
        )}
      </td>
      <td>
        <select value={f.zahlungsweg} onChange={(e) => set("zahlungsweg", e.target.value)}>
          <option value="inhaber">Inhaber</option>
          <option value="gbr">GbR-Konto</option>
        </select>
        {f.zahlungsweg === "inhaber" && (
          <select value={f.inhaber_id} onChange={(e) => set("inhaber_id", e.target.value)}>
            <option value="">Inhaber wählen…</option>
            {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
      </td>
      <td className="is-num">
        <input
          value={f.netto}
          onChange={(e) => set("netto", e.target.value)}
          inputMode="decimal"
          placeholder="0,00"
          style={{ width: 90, textAlign: "right" }}
        />
      </td>
      <td className="is-aktion">
        <button
          type="button"
          className="vg-icon-btn is-ok"
          title="Speichern"
          disabled={!kannSpeichern}
          onClick={() =>
            onSpeichern({
              netto: betrag,
              ausgabedatum: f.ausgabedatum,
              lieferant: f.lieferant,
              beschreibung: f.beschreibung,
              zahlungsweg: f.zahlungsweg,
              inhaber_id: f.zahlungsweg === "inhaber" ? f.inhaber_id : "",
              ausgabe_typ: typ === "verpflegung" ? "verpflegung" : f.ausgabe_typ,
              helfer_id: f.ausgabe_typ === "helfer" ? f.helfer_id : "",
            })
          }
        >
          ✓
        </button>
        <button type="button" className="vg-icon-btn" title="Abbrechen" onClick={onAbbrechen}>
          ✕
        </button>
      </td>
    </tr>
  );
}
